import React from 'react';
import {useTranslation} from 'react-i18next';
import {
   ControlButton,
   Glyphs,
   TscButtonGlyphPlacement,
   TscButtonType,
   TscThemeNames
} from '@techsmith/tsc-cloud-style-guide';
import styled from 'styled-components';
import TabHeader from '../TabHeader';
import AddCaptionTrackModal from '../../modals/AddCaptionTrackModal';
import DataTable, {ITableRowContent} from '../DataTable';
import {CaptioningState, CaptioningTypes, ICaptionData} from '../../../model/captionModels';
import captionApi from '../../../service/captionApi';
import {IWorkerState, SetupWorkerState} from '../../../model/workerState';
import dayjs from 'dayjs';
import {StyledWarning} from '../../util/StyledElements';
import CaptionsActions from './CaptionsActions';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {useTscTheme} from '../../../context/themeProvider';
import {mediaStore} from '../../../context/mediaProvider';
import {captionDataStore, updateCaptionDataState} from '../../../context/captionDataProvider';

const Container = styled.div<{theme: TscThemeNames}>`
   margin-top: 16px;
   padding: 0 16px;
`;

Container.displayName = 'captions-container';

const AddCaptionButton = styled(ControlButton)`
   width: 2rem;
`;

const DataTableWrapper = styled.div`
   .data-table-row, .data-table-header {
      grid-template-columns: repeat(3, 1fr) 10rem;
      grid-gap: 10px;
   }

   ul {
      min-height: 3rem;
      grid-template-columns: repeat( auto-fit, minmax(16.5rem, 1fr) );
   }
`;

export const CaptionsBase: React.FC<IWorkerState & IStateMappedProps> = ({isWorking: initialIsWorking, hasError: initialHasError, MediaId, CaptioningType, captionData}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [isModalDisplayed, setIsModalDisplayed] = React.useState(false);
   const [workerState, setWorkerState] = SetupWorkerState(initialIsWorking, initialHasError);

   React.useEffect(() => {
      setWorkerState({isWorking: initialIsWorking, hasError: initialHasError});
   }, [initialIsWorking, initialHasError]);

   const fetchCaptioningStatus = async () => {
      setWorkerState({isWorking: workerState.isWorking, hasError: false});
      try {
         updateCaptionDataState(await captionApi.getStatus(MediaId));
         setWorkerState({isWorking: false, hasError: false});
      } catch (e) {
         setWorkerState({isWorking: false, hasError: true});
      }
   };

   React.useEffect(() => {
      void fetchCaptioningStatus();
   }, []);

   const captionsTableColumnLabels = [
      t('accessibility.captions.status'),
      t('accessibility.captions.lastModifiedBy'),
      t('accessibility.captions.lastModified'),
      t('accessibility.captions.actions')
   ];

   const getTranslatedCaptionState = (status: CaptioningState): string => {
      switch (status) {
         case CaptioningState.Unknown: {
            return t('accessibility.captions.captionStatuses.unknown');
         } case CaptioningState.None: {
            return t('accessibility.captions.captionStatuses.none');
         } case CaptioningState.Transcribing: {
            return t('accessibility.captions.captionStatuses.transcribing');
         } case CaptioningState.ReadyForEdit: {
            return t('accessibility.captions.captionStatuses.readyForEdit');
         } case CaptioningState.Editing: {
            return t('accessibility.captions.captionStatuses.editing');
         } case CaptioningState.Published: {
            return t('accessibility.captions.captionStatuses.published');
         } case CaptioningState.PendingApproval: {
            return t('accessibility.captions.captionStatuses.pendingApproval');
         } case CaptioningState.Error: {
            return t('accessibility.captions.captionStatuses.error');
         } default: {
            return '';
         }
      }
   };

   const createTableRows = (): ITableRowContent[] => captionData.filter(c => c.CaptioningState !== CaptioningState.None).map((c, index) => ({
      tableRow: [
         getTranslatedCaptionState(c.CaptioningState),
         CaptioningType === CaptioningTypes.AutoTranscribed && c.CaptioningState !== CaptioningState.Editing ? t('accessibility.captions.autoGenerated') : c.LastUpdatedByUser.DisplayName,
         dayjs(c.LastUpdateDateTime).format('MM/DD/YYYY'),
         <CaptionsActions
            captionData={c}
            setParentWorkerState={setWorkerState}
            refreshCaptionData={fetchCaptioningStatus}
            key={index}
         />
      ]
   }));

   const captionTracksLength = captionData.filter(c => c.CaptioningState !== CaptioningState.None).length;
   const captionTrackLabel = captionTracksLength === 0 ? t('accessibility.captions.noCaptionTracks') :
      captionTracksLength === 1 ? t('accessibility.captions.oneCaptionTrack') :
         t('accessibility.captions.multipleCaptionTracks', {tracks: captionTracksLength});

   const addCaptionsButton = (
      <AddCaptionButton
         title={t('accessibility.captions.addCaptionTrack')}
         buttonType={TscButtonType.secondary}
         themeName={theme}
         onClick={() => setIsModalDisplayed(!isModalDisplayed)}
         glyph={<Glyphs.Add16x16 />}
         glyphPlacement={TscButtonGlyphPlacement.left}
         disabled={workerState.isWorking || !!captionTracksLength}
         width="13.5rem"
         testId="add-caption-button"
      />
   );

   return (
      <Container
         data-test-id="captions-container"
         theme={theme}
      >
         <TabHeader
            leftLabel={t('accessibility.captions.label')}
            rightLabel={captionTrackLabel}
            hideRightLabelInMobileView={true}
            button={addCaptionsButton}
         />
         {workerState.hasError && (
            <StyledWarning>
               {t('accessibility.captions.captionDataFetchError')}
            </StyledWarning>
         )}
         {!!captionTracksLength && (
            <DataTableWrapper>
               <DataTable
                  columnLabels={captionsTableColumnLabels}
                  tableRows={createTableRows()}
                  emptyStateLabel={t('accessibility.captions.noCaptionsText')}
                  isLoading={workerState.isWorking}
               />
            </DataTableWrapper>
         )}
         {isModalDisplayed && (
            <AddCaptionTrackModal
               refreshCaptionData={fetchCaptioningStatus}
               onClose={() => setIsModalDisplayed(!isModalDisplayed)}
            />
         )}
      </Container>
   );
};

export interface IStateMappedProps {
   MediaId: string;
   CaptioningType: CaptioningTypes;
   captionData: ICaptionData[];
}

const mapStatesToProps = (
   {isWorking, hasError}: IWorkerState,
   {MediaId, CaptioningType}: Partial<IStateMappedProps>,
   {captionData}: Partial<IStateMappedProps>
): IStateMappedProps & IWorkerState => ({isWorking, hasError, MediaId, CaptioningType, captionData});

export default withMemoizedContexts(mapStatesToProps, mediaStore, captionDataStore)(CaptionsBase);
